import {useSearchParamStructured} from '@raison/navigation';
import {type} from 'arktype';
import React, {useState} from 'react';
import {Navigate, useNavigate} from 'react-router';
import {GenericErrorMsg} from '~/entities/dialogs';
import {AccountId, MethodId, MethodType} from '~/entities/operations-fiat';
import {executeOperationFinishNavigation} from '~/entities/transactions-history';
import {ErroneousServerResult} from '~/library/network-facade';
import {TransactionId, TransactionIdArkType} from '~/library/types';
import {RsnAmount, RsnDialog} from '~/library/ui-kit/src/Components';
import {OrderPlacedInfoPage} from '~/processes/fiat-operations/ui/Shared/OrderPlacedInfoPage';
import {OperableAccount, OperableBank, OperationModel} from '~/redux/finances/domain';
import {useAppDispatch} from '~/shared/store';
import {useNavigateToOperation} from '../../lib/useNavigateToOperation';
import {NotEnoughFundsDialog} from './NotEnoughFundsDialog';
import {WithdrawIntro} from './WithdrawIntro';
import {WithdrawSepaRequisites} from './WithdrawSepaRequisites';
import {WithdrawSwiftRequisites} from './WithdrawSwiftRequisites';

const parseParam = type.never
    .or({
        step: 'undefined?',
    })
    .or({
        step: '"requisites"',
    })
    .or({
        step: '"finish"',
        orderId: TransactionIdArkType,
    });
const decodeParam = parseParam.assert.bind(parseParam);

export const WithdrawParams = type({
    accountId: AccountId,
    methodType: MethodType.optional(),
    methodId: MethodId.optional(),
});
export type WithdrawParams = typeof WithdrawParams.infer;

export type WithdrawPageProps = {
    providerName: string;
    accounts: OperableAccount[];
    methods: OperableBank[];
    account: OperableAccount;
    method?: OperableBank;
    operations: OperationModel[];
};

export function WithdrawPage({providerName, accounts, account, methods, method, operations}: WithdrawPageProps) {
    const navigate = useNavigate();
    const {navigateToOperation, createOperationPath} = useNavigateToOperation('withdraw', operations);
    const [introCompleted, setIntroCompleted] = useState(false);

    const [amount, setAmount] = useState(RsnAmount.empty);

    const dispatch = useAppDispatch();

    const [direction, setDirection] = useSearchParamStructured('withdraw', {
        decode: decodeParam,
        fallback: {},
    });

    const [modal, setModal] = useState<'not-enough-funds' | null>(null);
    const closeModal = () => setModal(null);

    const handleSuccess = (id: TransactionId) => {
        setDirection({
            step: 'finish',
            orderId: id,
        });
    };

    const handleProblem = (problem: ErroneousServerResult) => {
        // On the intro step the same problem is shown as an issue under the amount input,
        // the dialog is left for the requisites step, where there is no amount input on screen.
        if (problem.message.startsWith('Not enough money')) {
            setModal('not-enough-funds');
            return;
        }
        dispatch(GenericErrorMsg.open({details: problem.message}));
    };

    const handleChangeStep = (amount: RsnAmount) => {
        setAmount(amount);
        setIntroCompleted(true);
        setDirection({step: 'requisites'});
    };

    function renderScreen() {
        if (!introCompleted && direction.step) {
            return <Navigate to={createOperationPath(account, method || null)} replace />;
        }

        if (direction.step === 'requisites' && method?.method === 'sepa') {
            return (
                <WithdrawSepaRequisites
                    account={account}
                    method={method}
                    amount={amount}
                    onSuccess={handleSuccess}
                    onProblem={handleProblem}
                />
            );
        }

        if (direction.step === 'requisites' && method?.method === 'swift') {
            return (
                <WithdrawSwiftRequisites
                    account={account}
                    method={method}
                    amount={amount}
                    onSuccess={handleSuccess}
                    onProblem={handleProblem}
                />
            );
        }

        if (direction.step === 'finish') {
            return (
                <OrderPlacedInfoPage
                    currency={{
                        code: account.currencyCode,
                        decimals: account.currencyDecimals,
                    }}
                    amount={RsnAmount.toString(amount)}
                    goToDetails={() => executeOperationFinishNavigation(direction.orderId, navigate)}
                    direction={'out'}
                    details={'Withdrawal request submitted.\nProcessing may take some time.'}
                />
            );
        }

        return (
            <WithdrawIntro
                providerName={providerName}
                account={account}
                method={method}
                accounts={accounts}
                methods={methods}
                onOperationChange={navigateToOperation}
                onStepChange={handleChangeStep}
                onProblem={handleProblem}
                initialAmount={amount}
            />
        );
    }

    return (
        <>
            {renderScreen()}

            <RsnDialog.Controller onClose={closeModal}>
                {modal === 'not-enough-funds' && <NotEnoughFundsDialog />}
            </RsnDialog.Controller>
        </>
    );
}
